import React from 'react'
import {withRouter} from 'react-router-dom'
import PropTypes from 'prop-types'
import {getChatHistoryRequest, getChatIdRequest} from '../api/websocketApi'
import {convertUserMessageToBepFormat} from '../api/messageConverter'
import {
  generatePostBackObject,
  getQueryVariable,
  sendUserEventToParent
} from '../actions/static'
import widgetStorage from '../actions/storage'
import WebSocketClient from '../api/WebSocketClient'
import {getMessagesFromHistory} from '../helper/getMessagesFromHistory'
import {getPostbackMessage} from '../helper/getPostbackMessage'
import {MESSAGE_SIZE} from '../constants'
import {getGreetingText, sendAction, sendOpenedGreeting, setUnauthenticatedHandler} from '../api/api'
import { clearSessionToken, getSessionToken } from '../config/config'
import {camelToSnakeCase} from '../helper/camelToSnakeCase'
import {actionType} from "../constants/messageActionType";
import {hasPayloadValue} from "../helper/paramUtil";

import { cloneDeep, isEqual } from 'lodash-es'
import {AGENT_JOINED, AGENT_LEFT, END_CONVERSATION, SESSION_EXPIRED,} from '../constants/actionsType'
import {READ} from '../constants/statuses'
import {persistentMenuItemType} from "../constants/string";
import { QUESTIONNAIRE_SWITCH_LANGUAGE } from '../constants/postMessageEvent'
import { datadogLogs } from '@datadog/browser-logs';
import { handleStartEvent, handleStopEvent } from '../datadog/datadogTimingEventHandler'

// A burst of 401s and STOMP refusals for one lost session must reset the chat once, not once per response.
const SESSION_RECOVERY_COOLDOWN_MS = 5000

export const WebSocketsSharedContext = React.createContext({ sendStatus: null })
export const WebSocketsSharedConsumer = WebSocketsSharedContext.Consumer

class WebSockets extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      messages: [],
      client: null,
      page: 0,
      loadMore: true,
      showTyping: false,
      initialized: false,
      userData: {
        name: 'Widget user',
        email: '',
        chatId: null
      },
      chatWithAgent: false,
    }
    this.websocketClient = new WebSocketClient()
    this.websocketClient.configureEventHandler(this.handleWidgetReceiveEvent)
    this.websocketClient.configureLanguageUpdateHandler(message => this.handleLanguageUpdate(message))
    this.websocketClient.configureNateraEventHandler(event => this.handleNateraEvent(event))
  }

  componentDidMount() {
    // The server no longer accepts a bare chatId, so a request or STOMP frame the session does not cover comes
    // back 401 / ERR_SECURITY_ACCESS_DENIED. Both mean the same thing here: this chat is no longer ours.
    setUnauthenticatedHandler(this.recoverSession)
    this.websocketClient.setAccessDeniedCallback(this.recoverSession)
    // document.addEventListener('visibilitychange', this.handleDocumentFocus)
    const chatId = widgetStorage.tryFindChatIdInAnyStorage()
    // const isOpen = widgetStorage.isWidgetOpened()
    this.setState({ chatId }, () => {
      if (chatId) {
        // Connect ahead of getStarted only when this tab already holds the credential for the chat; otherwise
        // the socket would be refused, and getStarted below connects once it knows which chat is ours.
        if (getSessionToken()) {
          this.websocketClient.disconnect()
          this.websocketClient.configureChatId(chatId)
          this.websocketClient.connect(this.props.botId, chatId, true)
        }
        this.initializeConversation()
      }
    })
  }

  componentDidUpdate(prevProps, prevState) {
    const {openWidget, isEndOfConversation, botId, isUserIdle, needToDisconnectWebSocketsConnection} = this.props
    const {userData: {chatId}} = this.state
    const isPortalSessionExpired = prevProps.isUserIdle !== isUserIdle;

    if (prevProps.openWidget !== openWidget || prevState.userData.chatId !== chatId) {
      const sendOpenWidgetEvent = openWidget && chatId && isEndOfConversation
      if (sendOpenWidgetEvent) {
        sendAction(actionType.OPEN_WIDGET, botId, chatId)
      }
    }
    if (isPortalSessionExpired) {
      this.setState({ showTyping: false })
      this.websocketClient.disconnect()
    }
    if (prevProps.needToDisconnectWebSocketsConnection !== needToDisconnectWebSocketsConnection) {
      needToDisconnectWebSocketsConnection && this.websocketClient.disconnect()
    }
  }

  componentWillUnmount() {
    setUnauthenticatedHandler(null)
    this.websocketClient.setAccessDeniedCallback(null)
    this.websocketClient.disconnect()
    this.props.closeWidget()
  }

  // handleDocumentFocus = () => {
  //   if (document.visibilityState === 'visible') {
  //     const { userData } = this.state
  //     const { botId, messages, websiteLocation } = this.props
  //     const messageId = messages[messages.length - 1]?.id
  //     this.websocketClient.sendStatus(
  //       'READ',
  //       messageId,
  //       botId,
  //       userData.chatId,
  //       websiteLocation,
  //     )
  //   }
  // }

  getChatId = message => {
    const { userData } = this.state
    const { user, botId, attributes, websiteLocation, greeting, nevaPreSessionToken, patientInfo, heapIoIdentity, heapIoPrefix, isWidgetPopup, hasPersistentMenu, popupWasShow, setChatId } = this.props
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const savedChatId = widgetStorage.tryFindChatIdInAnyStorage()

    const data = {
      botId,
      chatId: savedChatId,
      name: user?.name || 'Widget user',
      email: user?.email || '',
      language: user?.language,
      attributes: attributes.map(att => ({
        value: att.value,
        name: camelToSnakeCase(att.name),
      })),
      metadata: {
        nevaPreSessionToken,
        patientInfo,
        timezone: timezone ? timezone : null,
        heapIoIdentity,
        heapIoPrefix
      }
    }

    if (!userData?.chatId) {
      getChatIdRequest(data)
        .then(chatId => {
          if (!chatId) {
            throw new Error('getStarted returned no chat')
          }
          // The server resumes savedChatId only when this tab's session covers it; otherwise it has started a new
          // chat, and everything below must move to that one.
          const isNewChat = String(chatId) !== String(savedChatId)
          if (isNewChat && savedChatId) {
            this.websocketClient.disconnect()
          }
          if (isNewChat) {
            data.chatId = chatId
            widgetStorage.setChatId(chatId)
            setChatId(chatId)
          }
          this.websocketClient.configureChatId(chatId)
          this.websocketClient.configureBotId(botId)
          // A no-op when componentDidMount already connected to this chat.
          const initiateConversation = isNewChat ? !greeting?.payload || !greeting.popupWasShow : true
          this.websocketClient.connect(botId, chatId, initiateConversation, isNewChat && Boolean(hasPersistentMenu))
          this.setState({ userData: data }, () => {
            this.getChatHistory(chatId)
          })
          return chatId
        })
        .then(chatId => {
          if (greeting && String(chatId) !== String(savedChatId) && popupWasShow) {
            sendOpenedGreeting(botId, greeting.hash, chatId)
          }
        })
        .then(() => {
          if (message) {
            const data = generatePostBackObject(message)
            this.postMessage(data)
          }
        })
        .catch(error => datadogLogs.logger.error('Can not start the chat', {}, error))
    }
  }

  /*
   * The session behind this chat is gone — expired, or never issued to this tab — so the chat cannot be continued
   * from here. Drop it the way the widget drops a finished chat; the next open starts a new one with a new
   * credential.
   */
  recoverSession = () => {
    if (this.isRecoveringSession) {
      return
    }
    this.isRecoveringSession = true
    datadogLogs.logger.info('Widget session is no longer valid; resetting the chat')

    clearSessionToken()
    this.websocketClient.disconnect()
    this.websocketClient.configureChatId(null)
    this.setState(prevState => ({
      chatId: null,
      userData: { ...prevState.userData, chatId: null },
      showTyping: false,
    }))
    this.props.setChatId?.(null)
    if (this.props.resetChat) {
      this.props.resetChat()
    } else {
      widgetStorage.clearStorage(this.props.botId)
    }

    setTimeout(() => {
      this.isRecoveringSession = false
    }, SESSION_RECOVERY_COOLDOWN_MS)
  }

  getPersistentMenu = (botId, chatId) => {
    const {hasPersistentMenu, updatePersistentMenu} = this.props

    if (hasPersistentMenu) {
      getPersistentMenuApi(botId, chatId)
        .then(response => updatePersistentMenu(response))
        .catch(datadogLogs.logger.error("Can not get persistent menu elements"))
    }
  }

  sendRefMessage = ref => {
    this.postMessage({
      type: 'postback',
      payload: ref,
    })
    widgetStorage.setRef(ref)
  }

  postMessage = message => {
    const { userData } = this.state
    const { websiteLocation, botId } = this.props

    handleStartEvent(message?.postback?.payload)

    const data = convertUserMessageToBepFormat(userData.chatId, message, websiteLocation)
    this.websocketClient.sendMessage(data, botId, userData.chatId)
  }

  getChatHistory = (chatId) => {
    if (this.props.messages.length > 0) {
      return
    }

    getChatHistoryRequest(this.props.botId, MESSAGE_SIZE).then(res => {
      const loadMore = res && res.length === MESSAGE_SIZE

      this.setState(
        {
          page: this.state.page + 1,
          loadMore,
        },
        () => {
          if (res && res.length) {
            this.setHistory(res)
          } else {
            this.props.setIsEnableAriaLive(true)
          }
        },
      )
    })
  }

  getMoreChatHistory = () => {
    if (this.state.loadMore && widgetStorage.isEnabled()) {
      getChatHistoryRequest(this.props.botId, MESSAGE_SIZE, this.state.page,)
        .then(res => {
          const loadMore = res && res.length === MESSAGE_SIZE

          this.setState(
            {
              page: this.state.page + 1,
              loadMore,
            },
            () => {
              if (res && res.length) {
                this.setMoreHistory(res)
              }
            },
          )
        })
        .catch(error => datadogLogs.logger.error(error))
    }
  }

  setHistory(history) {
    this.props.updateMessages(getMessagesFromHistory(history))

    const ref = getQueryVariable('ref')
    if (ref !== 'false' && ref !== widgetStorage.getRef()) {
      this.sendRefMessage(ref)
    }
  }

  setMoreHistory(history) {
    // this.setState(
    //   {
    //     messages: [...this.state.messages, history],
    //   },
    //   () => {
    //     this.props.updateMessages(
    //       getMessagesFromHistory(history, isPanorama),
    //       undefined,
    //       true,
    //     )
    //   },
    // )
    this.props.updateMessages(getMessagesFromHistory(history), undefined, true)
  }

  handleWidgetReceiveEvent = event => {
    // const { message, actions, isFromBot } = event
    // const { processMessage, userData } = this.state
    const { message, actions, isFromBot, status } = event
    if (actions) {
      actions.forEach(action => this.handleAction(action))
    }

    if (message) {
      this.handleWidgetReceivedMessages(message, isFromBot, actions)
    }

    if (status?.status === READ && !isFromBot) {
      this.handleWidgetReceivedStatus(status)
    }
  }

  handleWidgetReceivedMessages = (message, isFromBot) => {
    const { processMessage } = this.state
    const {
      botId,
      websiteLocation,
      isHiddenWidget,
      setUnreadMessages,
      unreadMessages,
      settings,
      firstUnreadMessageId,
      setFirstUnreadMessageId,
      handleAddUnreadMessages,
      messages
    } = this.props

    let copyMessage = Object.assign({}, message)
    copyMessage.isFromBot = !!isFromBot

    if (copyMessage.type === 'postback') {
      copyMessage = getPostbackMessage(message)
    }

    if (isFromBot && messages.length) {
      if (isHiddenWidget) {
        setUnreadMessages(unreadMessages + 1)
        if (settings.widgetSettings.doShowPopupMessagePreview) {
          handleAddUnreadMessages(copyMessage)
        }
      }
      if (isHiddenWidget && !firstUnreadMessageId) {
        setFirstUnreadMessageId(copyMessage.id)
      }
      this.updatePreviousMessageStatus()
    }

    if(!this.isRequiredToSkipMessagesUpdate(message)){
      processMessage([copyMessage])
    }

  }

  updatePreviousMessageStatus = () => {
    const { messages } = this.props

    if (messages.length) {
      const messagesCopy = cloneDeep(messages)
      for (let i = messagesCopy.length - 1; i > 0; i--) {
        if (messagesCopy[i]?.isFromBot) {
          break
        } else {
          if (messagesCopy[i]) {
            messagesCopy[i].status = READ
          }
        }
      }
      this.props.replaceMessages(messagesCopy)
    }
  }

  handleAction(action) {
    const {processMessage, processTyping} = this.state
    const {type, metadata} = action
    const {
      setInputField,
      updatePersistentMenu,
      toggleWidget,
      setIsNeedCloseWidget,
      setIsEndOfConversation,
      setIsOpenSwitchLanguageModal,
      updateWidget,
      botId,
      chatId
    } = this.props

    if ([AGENT_JOINED, AGENT_LEFT, SESSION_EXPIRED].includes(action.type)) {
      processMessage([{ action }])
    }

    switch (type) {
      case actionType.TYPING_ON:
        processTyping()
        break
      case actionType.SHOW_INPUT:
        setInputField(true)
        break
      case actionType.OPEN_RATING:
        this.openRatingHandler()
        break
      case actionType.CLOSE_WIDGET_WITHOUT_CONFIRMATION:
        toggleWidget();
        this.endConversationBotLoaderNotifier();
        break
      case actionType.CLOSE_WIDGET_WITH_CONFIRMATION:
        setIsNeedCloseWidget(true, metadata.shouldShowClosePopupScheduleGCText)
        break
      case actionType.END_CONVERSATION:
        setIsEndOfConversation(true)
        break
      case actionType.UNDO_END_CONVERSATION:
        setIsEndOfConversation(false)
        break
      case actionType.UPDATE_PERSISTENT_MENU:
        updatePersistentMenu(JSON.parse(metadata.persistentMenuDTO))
        break
      case actionType.UPDATE_USER_INPUT:
        const defaultText = metadata.defaultText
        const isTextDisabled = JSON.parse(metadata.isTextDisabled)
        setInputField({isTextDisabled, defaultText})
        break
      case actionType.SWITCH_LANGUAGE:
        setIsOpenSwitchLanguageModal(true)
        break
      case actionType.WIDGET_LANGUAGE_SWITCHED:
        this.languageSwitcherBotLoaderNotifier(metadata.languageCode)
        break
      case actionType.DD_TIMING_EVENT_STOP:
        handleStopEvent(metadata.eventName)
        break
      case actionType.CLOSE_WIDGET:
        this.closeWidgetBotLoaderNotifier()
        break
    }
  }

  openRatingHandler() {
    const {settings, setIsOpenFeedbackModal} = this.props

    if (settings?.widgetSettings?.doAskForRating) {
      setIsOpenFeedbackModal(true)
    } else {
      this.handleResetChat()
    }
  }

  isRequiredToSkipMessagesUpdate(message) {
    const postback = message.postback;
    return postback
        && (postback.payload === 'Welcome back'
            || message.type === persistentMenuItemType.PERSISTENT_MENU_POSTBACK
            || hasPayloadValue(postback.payload, 'updateMessages', 'false'))
  }

  handleNateraEvent = event => {
    if (event.chat_event_data) {
      sendUserEventToParent(event.chat_event_data)
    }
  }

  handleWidgetReceivedStatus = data => {
    let messages = cloneDeep(this.props.messages)
    messages = messages.map(message => {
      if (message.id === data.messageId) {
        message.status = data.status
      }
      return message
    })

    if (!isEqual(messages, this.props.messages)) {
      this.props.replaceMessages(messages)
    }
  }

  handleLanguageUpdate = message => {
    const { updateWidget, widgetIframeSource } = this.props
    const language = message.shortName

    updateWidget(language)
    this.languageSwitchingQuestionnaireUpdate(widgetIframeSource, language)
    this.sendIFrameTitleToParent(language)
  }

  languageSwitcherBotLoaderNotifier = (language) => {
    window.parent.postMessage({
      type: 'widget-language-changed',
      languageCode: language,
    }, "*")
  }

  languageSwitchingQuestionnaireUpdate = (widgetIframeSource, language) => {

    if (widgetIframeSource) {
      widgetIframeSource.postMessage({
        type: QUESTIONNAIRE_SWITCH_LANGUAGE,
        language
      }, '*')
    }
  }

  sendIFrameTitleToParent = (language) => {
    window.parent.postMessage({
      type: 'widget-change-iframe-title',
      language
    }, '*')
  }

  endConversationBotLoaderNotifier() {
    window.parent.postMessage({
      type: 'widget-end-conversation',
    }, "*")
  }

  closeWidgetBotLoaderNotifier() {
    window.parent.postMessage({
      type: 'widget-close'
    }, '*')
  }

  handleResetChat = () => {
    const { resetChat } = this.props
    resetChat()
    this.websocketClient.disconnect()
  }

  initializeConversation = message => {
    this.setState(
      {
        processTyping: this.receiveTyping,
        processMessage: this.receiveMessage,
      },
      () => this.getChatId(message),
    )
  }

  userInteractionPostMessage = data => {
    const { setFirstUnreadMessageId, botId, setIsEnableAriaLive } = this.props
    const { userData, chatWithAgent } = this.state
    widgetStorage.setRef(null)
    setFirstUnreadMessageId(null)
    setIsEnableAriaLive(true)

    if (data.type === 'postback' && chatWithAgent) {
      sendAction(END_CONVERSATION, botId, userData.chatId)
        .then(() => this.postMessage(data))
        .then(() => this.finishChatWithAgent())
        .catch(error => datadogLogs.logger.error(error))
    } else {
      this.postMessage(data)
    }
  }

  receiveTyping = () => {
    if (!this.state.showTyping) {
      this.setState({ showTyping: true })
    }
  }

  receiveMessage = message => {
    this.props.updateMessages(message)
    this.setState({ showTyping: false })
  }

  updateTyping = showTyping => {
    this.setState({ showTyping })
  }

  sendStatus = (status, messageId) => {
    const { userData } = this.state
    const { botId, websiteLocation } = this.props
    this.websocketClient.sendStatus(status, messageId, botId, userData.chatId, websiteLocation)
  }

  finishChatWithAgent = () => {
    this.setState({ chatWithAgent: false })
  }

  render() {
    return (
      <WebSocketsSharedContext.Provider
        value={{
          postMessage: this.userInteractionPostMessage,
          startData: this.state?.userData,
          chatId: this.state.userData?.chatId,
          showTyping: this.state.showTyping,
          botId: this.props.botId,
          initializeConversation: this.initializeConversation,
          getMoreChatHistory: this.getMoreChatHistory,
          updateTyping: this.updateTyping,
          sendStatus: this.sendStatus,
          finishChatWithAgent: this.finishChatWithAgent,
        }}>
        {this.props.children}
      </WebSocketsSharedContext.Provider>
    )
  }
}

WebSockets.propTypes = {
  user: PropTypes.object,
  onConnect: PropTypes.func,
}

export default withRouter(WebSockets)
